import React, { useContext } from "react";
import PropTypes from "prop-types";
import DropdownMenu from "react-dd-menu";
import ButtonIcon from "Components/buttonIcon/ButtonIcon";
import WarningBanner from "Components/warningBanner/WarningBanner";
import QboxmailApi from "@/qboxmailApi";
import LoaderIcon from "Components/loader/LoaderIcon";

import {
  Icon,
  menu,
  adminColor,
  mailColor,
  settingsColor,
  logoutColor,
  stars0,
  stars1,
  stars2,
  stars3,
  medal,
  mailbox,
  warningFill,
  emailDelivery,
  videoColor,
} from "Icons/index";
import cx from "classnames";
import s from "./navbar.scss";
import t from "Utils/translation";
import Logo from "Components/logo/Logo";
import { withRouter } from "react-router-dom";
import Tooltip from "Components/tooltip/Tooltip";
import sessionStore from "@/stores/session";
import { view } from "@risingstack/react-easy-state";
import BetaLabel from "../betaLabel/BetaLabel";
import { warningFillYellow } from "../../icons";
import { TourContext } from "ReactContext/index";

const TourStart = () => {
  const { setRunTour, canRunTour } = useContext(TourContext);
  return canRunTour ? (
    <li>
      <a onClick={() => setRunTour(true)}>
        <Icon svg={videoColor} />
        <div>{t("ddMenu.tour")}</div>
      </a>
    </li>
  ) : null;
};

const ContextType = {
  getLoggedUser: PropTypes.func,
  isMobileDevice: PropTypes.func,
  isMediaMobile: PropTypes.func,
  isMediaSM: PropTypes.func,
  isMediaMD: PropTypes.func,
  isMediaLG: PropTypes.func,
};

const PRICES_ICONS = {
  stars0,
  stars1,
  stars2,
  stars3,
  medal,
};

class Navbar extends React.Component {
  constructor(props, context) {
    super(props);

    const loggedUser = context.getLoggedUser();
    let canSeeDelivery =
      loggedUser && (loggedUser.type === "Admin" || loggedUser.type === "Customer" || loggedUser.type === "Manager");

    if (loggedUser && loggedUser.type === "TeamMember" && loggedUser.permissions.includes("see_delivery")) {
      canSeeDelivery = true;
    }

    const env = process.env.NODE_ENV;
    if (env === "production" && location.hostname === "sandbox.qboxmail.com") canSeeDelivery = false;

    this.state = {
      isMenuOpen: false,
      loggedUser,
      canSeeDelivery,
      loadingInvoiceUrl: false,
    };
  }

  goToEmailAccount = () => {
    const { loggedUser } = this.state;

    if (loggedUser) {
      this.props.history.push("/email_account/settings");
    }
  };

  closeUserMenu = () => {
    this.setState({ isMenuOpen: false });
  };

  toggleUserMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  };

  onTrialBannerClick = () => {};

  /**
   * Handles the click event for unpaid invoices.
   * @returns {Promise<Boolean>}
   */
  onUnpaidInvoiceClick = async () => {
    this.setState({ loadingInvoiceUrl: true });
    const api = new QboxmailApi({ useCookie: true });
    try {
      const response = await api.generateWHMCSUrl("show_invoice");
      window.open(response.resources[0].url, "_blank");
    } catch (error) {
      console.log("error generating whmcs invoice", error);
    }

    this.setState({ loadingInvoiceUrl: false });

    return true;
  };

  //eslint-disable-next-line no-unused-vars
  showDelivery = (e) => {
    const { loggedUser } = this.state;

    if (loggedUser.type !== "Customer") {
      return this.props.history.push("/delivery");
    }

    const isPayingForHosting =
      (loggedUser.plan === "paid" && loggedUser.status_detail !== "wait_pay") || loggedUser.plan === "free";

    if (!isPayingForHosting) {
      return this.props.history.push("/delivery/access-request");
    }

    return this.props.history.push("/delivery");
  };

  render() {
    let { className, onClickMenu, hideMenuIcon, logout, history, pricesIcon, isDelivery, isSettings } = this.props;
    const { loggedUser, canSeeDelivery } = this.state;
    const { isMediaMobile } = this.context;

    const waitingFirstPayment =
      loggedUser.plan === "paid" &&
      loggedUser.status_detail === "wait_pay" &&
      loggedUser.orders[0]?.status === "pending";

    return (
      <div className={cx(className, s.navbar)}>
        <div
          className={s.iconMenu}
          style={{
            opacity: hideMenuIcon ? 0 : 1,
            pointerEvents: hideMenuIcon ? "none" : "auto",
          }}
        >
          <ButtonIcon icon={menu} iconSize="1.25rem" onClick={onClickMenu} notHover />
        </div>

        <a href="/">
          <Logo className={s.logo} />
        </a>

        {canSeeDelivery && (
          <div className={s.appSwitchContainer}>
            <div
              //eslint-disable-next-line
              onClick={(e) => history.push("/")}
              className={cx(s.appSwitchButton, {
                [s.appSwitchButtonSelected]: !isSettings && !isDelivery,
              })}
            >
              <Icon svg={mailbox} width="1rem" height="1rem" />
              <div className="text-sm">{t("navbar.appSwitch.mailboxes")}</div>
            </div>
            <div className={s.appSwitchSeparator} />
            <div
              onClick={this.showDelivery}
              className={cx(s.appSwitchButton, {
                [s.appSwitchButtonSelected]: !isSettings && isDelivery,
              })}
            >
              <Icon svg={emailDelivery} width="1rem" height="1rem" />

              <div className="text-sm">Email Delivery</div>
              <BetaLabel />
            </div>
          </div>
        )}

        {pricesIcon && (
          <Tooltip title={t("prices.navbarTooltipTitle")} text={t("prices.navbarTooltipDesc")} className={s.prices}>
            <Icon svg={PRICES_ICONS[pricesIcon]} height="1.5rem" onClick={() => this.props.history.push("/prices")} />
          </Tooltip>
        )}

        {sessionStore.mtmPendingRequests.length > 0 && (
          // <Tooltip title={t("prices.navbarTooltipTitle")} text={t("prices.navbarTooltipDesc")} className={s.mtm}>
          <WarningBanner msg={t("mtm.pendingRequests")} onClick={() => this.props.history.push("/mtm_requests")} />
          // </Tooltip>
        )}

        {loggedUser.plan === "trial" && loggedUser.days_until_expiration > 0 && (
          <div className={s.trialCountdownBanner} onClick={this.onTrialBannerClick}>
            <WarningBanner
              msg={t(
                "navbar.trial.warningMsg",
                null,
                { daysLeft: loggedUser.days_until_expiration },
                loggedUser.days_until_expiration,
              )}
            />
          </div>
        )}

        {waitingFirstPayment && (
          <div
            className={cx(
              "px-4 py-2 rounded bg-[--yellow-l35] text-sm font-semibold text-[--yellow-d30] md:flex gap-2 items-center mr-8 hidden justify-self-end",
            )}
          >
            <Icon className={cx("opacity-[.75]", {})} svg={warningFill} width="1rem" height="1rem" />

            {t("navbar.trial.waitPay")}

            <button
              className={cx(
                "border-0 font-semibold bg-[--green] rounded py-1 px-2 text-white text-xs cursor-pointer justify-center",
                {
                  "opacity-50 cursor-not-allowed pointer-events-none": this.state.loadingInvoiceUrl,
                },
              )}
              onClick={this.onUnpaidInvoiceClick}
            >
              {this.state.loadingInvoiceUrl ? (
                <LoaderIcon iconSize="0.875rem" color="#fff" />
              ) : (
                t("button.see.proforma")
              )}
            </button>
          </div>
        )}

        {loggedUser.plan === "trial" && loggedUser.days_until_expiration <= 0 && (
          <div className={s.trialCountdownBanner} onClick={this.onTrialBannerClick}>
            <WarningBanner msg={t("navbar.trial.end.warningMsg")} />
          </div>
        )}

        <div className={cx(s.user, "tour-start-container")} onClick={this.toggleUserMenu}>
          {!isMediaMobile() && (
            <div className="flex flex-col mr-2 text-sm">
              <span className="text-xs text-[--text-a40] capitalize relative top-[4px]">{loggedUser.type_label}</span>
              {`${loggedUser.fullname || loggedUser.email_address}`}
            </div>
          )}
          <Icon className={s.userIcon} svg={adminColor} width="1.5rem" height="1.5rem" />
          <DropdownMenu
            className="navbar-ddMenu"
            isOpen={this.state.isMenuOpen}
            close={this.closeUserMenu}
            toggle={<span />}
            animate={false}
            align="right"
          >
            {loggedUser.linked_email_account && (
              <li className="account">
                <a onClick={this.goToEmailAccount}>
                  <Icon svg={mailColor} />
                  <div>
                    <span>{t("ddMenu.manage.account")}</span>
                    <span>{loggedUser.email_address}</span>
                  </div>
                </a>
              </li>
            )}

            {loggedUser.plan === "trial" && isMediaMobile() && loggedUser.days_until_expiration > 0 && (
              <li>
                <a onClick={this.onTrialBannerClick}>
                  <Icon svg={warningFillYellow} />
                  <div>
                    {t(
                      "navbar.trial.warningMsg",
                      null,
                      { daysLeft: loggedUser.days_until_expiration },
                      loggedUser.days_until_expiration,
                    )}
                  </div>
                </a>
              </li>
            )}
            {loggedUser.plan === "trial" && isMediaMobile() && loggedUser.days_until_expiration <= 0 && (
              <li>
                <a onClick={this.onTrialBannerClick}>
                  <Icon svg={warningFillYellow} />
                  <div>{t("navbar.trial.end.warningMsg")}</div>
                </a>
              </li>
            )}
            <li>
              <a onClick={() => this.props.history.push("/account")}>
                <Icon svg={settingsColor} />
                <div>{t("ddMenu.settings")}</div>
              </a>
            </li>
            <TourStart />
            <li>
              <a onClick={logout}>
                <Icon svg={logoutColor} />
                <div>{t("ddMenu.logout")}</div>
              </a>
            </li>

            {isMediaMobile() && (
              <li className="cancelBtn">
                <a>
                  <div>{t("ddMenu.cancel")}</div>
                </a>
              </li>
            )}
          </DropdownMenu>
        </div>
      </div>
    );
  }
}

Navbar.contextTypes = ContextType;

export default withRouter(view(Navbar));
